import React, { useState, useEffect, useMemo } from 'react';
import { getActiveFiscalYearId } from '../../services/fiscalYearStorage';   // default to this tab's selected year
import {
  Dialog, DialogTitle, DialogContent, Box, Paper, Typography,
  TextField, MenuItem, Button, IconButton, Tooltip, FormControlLabel, Checkbox,
  Chip, CircularProgress, Collapse, Divider, Slider, useTheme, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Close, Refresh as RefreshIcon, FileDownload as ExcelIcon, PictureAsPdf as PdfIcon,
  Print as PrintIcon, AccountTree, KeyboardArrowDown, KeyboardArrowRight,
  UnfoldMore, UnfoldLess, Fullscreen, FullscreenExit, ChevronLeft,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useLanguage } from '../../contexts/LanguageContext';
import { financialReportsService, TrialBalanceFilter, TrialBalanceReportResult, TrialBalanceDto } from '../../services/financialReports.service';
import { fiscalPeriodsService } from '../../services/fiscalPeriods.service';
import { companyInfoService } from '../../services/companyInfo.service';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';

interface FiscalPeriod {
  fiscalPeriodId: number;
  periodName: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
}

interface CompanyInfo {
  companyName?: string;
  companyNameAr?: string;
  logoBase64?: string | null;
}

interface TreeNode extends TrialBalanceDto {
  children: TreeNode[];
  hasChildren: boolean;
  aggregatedOpeningDebit: number;
  aggregatedOpeningCredit: number;
  aggregatedPeriodDebit: number;
  aggregatedPeriodCredit: number;
  aggregatedClosingDebit: number;
  aggregatedClosingCredit: number;
  aggregatedBalance: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const translations = {
  en: {
    title: 'Hierarchical Trial Balance',
    fiscalPeriod: 'Fiscal Period',
    selectPeriod: 'Select Period',
    fromDate: 'From',
    toDate: 'To',
    levels: 'Levels',
    includeZero: 'Zero Balances',
    expandAll: 'Expand',
    collapseAll: 'Collapse',
    refresh: 'Load',
    exportExcel: 'Excel',
    exportPdf: 'PDF',
    print: 'Print',
    close: 'Close',
    code: 'Code',
    accountName: 'Account Name',
    openingDr: 'Opening Dr',
    openingCr: 'Opening Cr',
    periodDr: 'Period Dr',
    periodCr: 'Period Cr',
    closingDr: 'Closing Dr',
    closingCr: 'Closing Cr',
    balance: 'Balance',
    total: 'TOTAL',
    noData: 'Click "Load" to fetch data',
    loading: 'Loading...',
    level: 'L',
    accounts: 'accounts',
    children: 'children',
  },
  ar: {
    title: 'ميزان المراجعة الهرمي',
    fiscalPeriod: 'السنة المالية',
    selectPeriod: 'اختر السنة',
    fromDate: 'من',
    toDate: 'إلى',
    levels: 'المستويات',
    includeZero: 'الأرصدة الصفرية',
    expandAll: 'توسيع',
    collapseAll: 'طي',
    refresh: 'تحميل',
    exportExcel: 'Excel',
    exportPdf: 'PDF',
    print: 'طباعة',
    close: 'إغلاق',
    code: 'الكود',
    accountName: 'اسم الحساب',
    openingDr: 'مدين افتتاحي',
    openingCr: 'دائن افتتاحي',
    periodDr: 'مدين الفترة',
    periodCr: 'دائن الفترة',
    closingDr: 'مدين ختامي',
    closingCr: 'دائن ختامي',
    balance: 'الرصيد',
    total: 'الإجمالي',
    noData: 'اضغط "تحميل" لجلب البيانات',
    loading: 'جاري التحميل...',
    level: 'م',
    accounts: 'حساب',
    children: 'فرعي',
  },
};

// ألوان المستويات - Light Mode
const levelStyles = {
  1: { bg: '#0D47A1', color: '#fff', fontWeight: 800, fontSize: '0.95rem' },
  2: { bg: '#1565C0', color: '#fff', fontWeight: 700, fontSize: '0.9rem' },
  3: { bg: '#1976D2', color: '#fff', fontWeight: 600, fontSize: '0.85rem' },
  4: { bg: '#42A5F5', color: '#000', fontWeight: 500, fontSize: '0.8rem' },
  5: { bg: '#E3F2FD', color: '#000', fontWeight: 400, fontSize: '0.8rem' },
};

// ألوان المستويات - Dark Mode
const levelStylesDark = {
  1: { bg: '#1565C0', color: '#fff', fontWeight: 800, fontSize: '0.95rem' },
  2: { bg: '#1976D2', color: '#fff', fontWeight: 700, fontSize: '0.9rem' },
  3: { bg: '#1E88E5', color: '#fff', fontWeight: 600, fontSize: '0.85rem' },
  4: { bg: '#2196F3', color: '#fff', fontWeight: 500, fontSize: '0.8rem' },
  5: { bg: '#263238', color: '#90CAF9', fontWeight: 400, fontSize: '0.8rem' },
};

const HierarchicalTrialBalanceModal: React.FC<Props> = ({ open, onClose }) => {
  const { language } = useLanguage();
  const theme = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';
  const isDark = theme.palette.mode === 'dark';
  const styles = isDark ? levelStylesDark : levelStyles;
  const { minDate, maxDate } = useFiscalDateRange();

  // State
  const [loading, setLoading] = useState(false);
  const [fullscreen, setFullscreen] = useState(true);
  const [reportData, setReportData] = useState<TrialBalanceReportResult | null>(null);
  const [fiscalPeriods, setFiscalPeriods] = useState<FiscalPeriod[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());

  // Filters
  const [fiscalPeriodId, setFiscalPeriodId] = useState<number | undefined>();
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [maxLevel, setMaxLevel] = useState<number>(5);
  const [includeZeroBalances, setIncludeZeroBalances] = useState(false);

  // Load initial data
  useEffect(() => {
    if (open) {
      loadFiscalPeriods();
      loadCompanyInfo();
    }
  }, [open]);

  // Auto load when fiscal period changes
  useEffect(() => {
    if (fiscalPeriodId && open) {
      loadReport();
    }
  }, [fiscalPeriodId]);

  const loadFiscalPeriods = async () => {
    try {
      const periods = await fiscalPeriodsService.getAll();
      setFiscalPeriods(periods);
      const current = (periods.find((p: FiscalPeriod) => String(p.fiscalPeriodId) === getActiveFiscalYearId()) ?? periods.find((p: FiscalPeriod) => p.isCurrent));
      if (current) setFiscalPeriodId(current.fiscalPeriodId);
    } catch (error) {
      console.error('Error loading fiscal periods:', error);
    }
  };

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Error loading company info:', error);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const filter: TrialBalanceFilter = {
        fiscalPeriodId,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        includeZeroBalances: true,
        onlyFinalAccounts: false,
        showOpeningBalance: true,
      };
      const result = await financialReportsService.getTrialBalance(filter);
      setReportData(result);
      buildTree(result.data);
      toast.success(`Loaded ${result.data.length} accounts`);
    } catch (error: any) {
      console.error('Error loading report:', error);
      toast.error(error.response?.data?.message || 'Error loading report');
    } finally {
      setLoading(false);
    }
  };

  // Build hierarchical tree
  const buildTree = (data: TrialBalanceDto[]) => {
    const nodeMap = new Map<number, TreeNode>();
    const roots: TreeNode[] = [];

    // Create nodes
    data.forEach(item => {
      nodeMap.set(item.accountId, {
        ...item,
        children: [],
        hasChildren: false,
        aggregatedOpeningDebit: item.openingDebit,
        aggregatedOpeningCredit: item.openingCredit,
        aggregatedPeriodDebit: item.periodDebit,
        aggregatedPeriodCredit: item.periodCredit,
        aggregatedClosingDebit: item.closingDebit,
        aggregatedClosingCredit: item.closingCredit,
        aggregatedBalance: item.balance,
      });
    });

    // Build parent-child relationships
    data.forEach(item => {
      const node = nodeMap.get(item.accountId)!;
      if (item.parentAccountId && nodeMap.has(item.parentAccountId)) {
        const parent = nodeMap.get(item.parentAccountId)!;
        parent.children.push(node);
        parent.hasChildren = true;
      } else {
        roots.push(node);
      }
    });

    // Calculate aggregates bottom-up
    const calcAggregates = (node: TreeNode): void => {
      if (node.children.length === 0) return;
      node.children.forEach(calcAggregates);
      node.aggregatedOpeningDebit = node.children.reduce((s, c) => s + c.aggregatedOpeningDebit, 0);
      node.aggregatedOpeningCredit = node.children.reduce((s, c) => s + c.aggregatedOpeningCredit, 0);
      node.aggregatedPeriodDebit = node.children.reduce((s, c) => s + c.aggregatedPeriodDebit, 0);
      node.aggregatedPeriodCredit = node.children.reduce((s, c) => s + c.aggregatedPeriodCredit, 0);
      node.aggregatedClosingDebit = node.children.reduce((s, c) => s + c.aggregatedClosingDebit, 0);
      node.aggregatedClosingCredit = node.children.reduce((s, c) => s + c.aggregatedClosingCredit, 0);
      node.aggregatedBalance = node.aggregatedClosingDebit - node.aggregatedClosingCredit;
    };
    roots.forEach(calcAggregates);

    // Sort by code
    const sortNodes = (nodes: TreeNode[]): TreeNode[] => {
      return nodes.sort((a, b) => a.accountCode.localeCompare(b.accountCode))
        .map(n => ({ ...n, children: sortNodes(n.children) }));
    };

    setTreeData(sortNodes(roots));
    setExpandedNodes(new Set(data.map(d => d.accountId)));
  };

  const toggleNode = (id: number) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const expandAll = () => reportData && setExpandedNodes(new Set(reportData.data.map(d => d.accountId)));
  const collapseAll = () => setExpandedNodes(new Set());

  const formatNum = (n: number) => n.toLocaleString('en-GB', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

  // Calculate totals
  const totals = useMemo(() => {
    if (!treeData.length) return null;
    return {
      openingDebit: treeData.reduce((s, n) => s + n.aggregatedOpeningDebit, 0),
      openingCredit: treeData.reduce((s, n) => s + n.aggregatedOpeningCredit, 0),
      periodDebit: treeData.reduce((s, n) => s + n.aggregatedPeriodDebit, 0),
      periodCredit: treeData.reduce((s, n) => s + n.aggregatedPeriodCredit, 0),
      closingDebit: treeData.reduce((s, n) => s + n.aggregatedClosingDebit, 0),
      closingCredit: treeData.reduce((s, n) => s + n.aggregatedClosingCredit, 0),
    };
  }, [treeData]);

  // Render tree rows
  const renderRows = (nodes: TreeNode[], depth: number = 0): React.ReactNode[] => {
    const rows: React.ReactNode[] = [];
    
    nodes.forEach(node => {
      if (node.accountLevel > maxLevel) return;
      
      if (!includeZeroBalances && 
          node.aggregatedClosingDebit === 0 && 
          node.aggregatedClosingCredit === 0 && 
          node.aggregatedBalance === 0) return;

      const isExpanded = expandedNodes.has(node.accountId);
      const hasVisibleChildren = node.children.some(c => c.accountLevel <= maxLevel);
      const isFinal = !node.hasChildren;
      const style = styles[node.accountLevel as keyof typeof styles] || styles[5];
      const indent = depth * 20;

      rows.push(
        <TableRow 
          key={node.accountId}
          sx={{
            bgcolor: style.bg,
            '&:hover': { bgcolor: isDark ? '#37474F' : '#BBDEFB' },
            transition: 'background-color 0.2s',
          }}
        >
          {/* Level */}
          <TableCell sx={{ 
            width: 50, color: style.color, fontWeight: style.fontWeight, fontSize: style.fontSize,
            textAlign: 'center', borderRight: `2px solid ${isDark ? '#455A64' : '#90CAF9'}`, py: 0.8,
          }}>
            {t.level}{node.accountLevel}
          </TableCell>

          {/* Code + Expand */}
          <TableCell sx={{ 
            width: 130, color: style.color, fontWeight: 'bold',
            borderRight: `1px solid ${isDark ? '#455A64' : '#BBDEFB'}`, py: 0.8,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {hasVisibleChildren ? (
                <IconButton size="small" onClick={() => toggleNode(node.accountId)} sx={{ color: style.color, p: 0.3 }}>
                  {isExpanded ? <KeyboardArrowDown fontSize="small" /> : 
                    (isRTL ? <ChevronLeft fontSize="small" /> : <KeyboardArrowRight fontSize="small" />)}
                </IconButton>
              ) : <Box sx={{ width: 24 }} />}
              {node.accountCode}
            </Box>
          </TableCell>

          {/* Account Name */}
          <TableCell sx={{ 
            color: style.color, fontWeight: style.fontWeight, fontSize: style.fontSize,
            borderRight: `2px solid ${isDark ? '#455A64' : '#90CAF9'}`, py: 0.8,
            pl: isRTL ? 1 : `${indent + 8}px`, pr: isRTL ? `${indent + 8}px` : 1,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {!isFinal && <AccountTree sx={{ fontSize: 16, opacity: 0.7 }} />}
              <span>{isRTL ? (node.accountNameAr || node.accountName) : node.accountName}</span>
              {node.hasChildren && (
                <Chip label={`${node.children.length} ${t.children}`} size="small" 
                  sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'rgba(255,255,255,0.2)', color: style.color }} />
              )}
            </Box>
          </TableCell>

          {/* Opening Debit */}
          <TableCell align="right" sx={{ 
            color: style.color, fontFamily: 'monospace', fontSize: style.fontSize,
            borderRight: `1px solid ${isDark ? '#37474F' : '#E3F2FD'}`,
            bgcolor: isDark ? 'rgba(76,175,80,0.1)' : 'rgba(76,175,80,0.05)', py: 0.8,
          }}>
            {formatNum(isFinal ? node.openingDebit : node.aggregatedOpeningDebit)}
          </TableCell>

          {/* Opening Credit */}
          <TableCell align="right" sx={{ 
            color: style.color, fontFamily: 'monospace', fontSize: style.fontSize,
            borderRight: `2px solid ${isDark ? '#455A64' : '#90CAF9'}`,
            bgcolor: isDark ? 'rgba(244,67,54,0.1)' : 'rgba(244,67,54,0.05)', py: 0.8,
          }}>
            {formatNum(isFinal ? node.openingCredit : node.aggregatedOpeningCredit)}
          </TableCell>

          {/* Period Debit */}
          <TableCell align="right" sx={{ 
            color: style.color, fontFamily: 'monospace', fontSize: style.fontSize,
            borderRight: `1px solid ${isDark ? '#37474F' : '#E3F2FD'}`,
            bgcolor: isDark ? 'rgba(76,175,80,0.15)' : 'rgba(76,175,80,0.08)', py: 0.8,
          }}>
            {formatNum(isFinal ? node.periodDebit : node.aggregatedPeriodDebit)}
          </TableCell>

          {/* Period Credit */}
          <TableCell align="right" sx={{ 
            color: style.color, fontFamily: 'monospace', fontSize: style.fontSize,
            borderRight: `2px solid ${isDark ? '#455A64' : '#90CAF9'}`,
            bgcolor: isDark ? 'rgba(244,67,54,0.15)' : 'rgba(244,67,54,0.08)', py: 0.8,
          }}>
            {formatNum(isFinal ? node.periodCredit : node.aggregatedPeriodCredit)}
          </TableCell>

          {/* Closing Debit */}
          <TableCell align="right" sx={{ 
            color: style.color, fontFamily: 'monospace', fontWeight: 'bold', fontSize: style.fontSize,
            borderRight: `1px solid ${isDark ? '#37474F' : '#E3F2FD'}`,
            bgcolor: isDark ? 'rgba(76,175,80,0.2)' : 'rgba(76,175,80,0.12)', py: 0.8,
          }}>
            {formatNum(isFinal ? node.closingDebit : node.aggregatedClosingDebit)}
          </TableCell>

          {/* Closing Credit */}
          <TableCell align="right" sx={{ 
            color: style.color, fontFamily: 'monospace', fontWeight: 'bold', fontSize: style.fontSize,
            borderRight: `2px solid ${isDark ? '#455A64' : '#90CAF9'}`,
            bgcolor: isDark ? 'rgba(244,67,54,0.2)' : 'rgba(244,67,54,0.12)', py: 0.8,
          }}>
            {formatNum(isFinal ? node.closingCredit : node.aggregatedClosingCredit)}
          </TableCell>

          {/* Balance */}
          <TableCell align="right" sx={{ 
            fontFamily: 'monospace', fontWeight: 'bold', fontSize: style.fontSize, py: 0.8,
            color: (isFinal ? node.balance : node.aggregatedBalance) > 0 
              ? (isDark ? '#81C784' : '#2E7D32')
              : (isFinal ? node.balance : node.aggregatedBalance) < 0 
                ? (isDark ? '#E57373' : '#C62828') : style.color,
            bgcolor: isDark ? 'rgba(33,150,243,0.15)' : 'rgba(33,150,243,0.1)',
          }}>
            {formatNum(isFinal ? node.balance : node.aggregatedBalance)}
          </TableCell>
        </TableRow>
      );

      if (isExpanded && hasVisibleChildren) {
        rows.push(...renderRows(node.children, depth + 1));
      }
    });

    return rows;
  };

  // Export to Excel
  const exportToExcel = async () => {
    if (!treeData.length) return;
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet(t.title);

    // Add Logo
    if (companyInfo?.logoBase64) {
      try {
        const imageId = workbook.addImage({ base64: companyInfo.logoBase64, extension: 'png' });
        ws.addImage(imageId, { tl: { col: 0, row: 0 }, ext: { width: 70, height: 70 } });
      } catch (e) { console.error('Error adding logo:', e); }
    }

    ws.mergeCells('B1:J1');
    ws.getCell('B1').value = companyInfo?.companyName || 'Company';
    ws.getCell('B1').font = { size: 14, bold: true };
    ws.getCell('B1').alignment = { horizontal: 'center' };

    ws.mergeCells('B2:J2');
    ws.getCell('B2').value = t.title;
    ws.getCell('B2').font = { size: 12, bold: true };
    ws.getCell('B2').alignment = { horizontal: 'center' };

    ws.addRow([]); // Empty row after header

    const headers = [t.level, t.code, t.accountName, t.openingDr, t.openingCr, t.periodDr, t.periodCr, t.closingDr, t.closingCr, t.balance];
    const headerRow = ws.addRow(headers);
    headerRow.eachCell(c => {
      c.font = { bold: true, color: { argb: 'FFFFFF' } };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1565C0' } };
      c.alignment = { horizontal: 'center' };
    });

    const flatten = (nodes: TreeNode[], rows: any[] = [], depth = 0): any[] => {
      nodes.forEach(n => {
        if (n.accountLevel <= maxLevel) {
          const isFinal = !n.hasChildren;
          rows.push([
            `L${n.accountLevel}`, n.accountCode,
            '  '.repeat(depth) + (isRTL ? n.accountNameAr : n.accountName),
            isFinal ? n.openingDebit : n.aggregatedOpeningDebit,
            isFinal ? n.openingCredit : n.aggregatedOpeningCredit,
            isFinal ? n.periodDebit : n.aggregatedPeriodDebit,
            isFinal ? n.periodCredit : n.aggregatedPeriodCredit,
            isFinal ? n.closingDebit : n.aggregatedClosingDebit,
            isFinal ? n.closingCredit : n.aggregatedClosingCredit,
            isFinal ? n.balance : n.aggregatedBalance,
          ]);
          if (n.children.length) flatten(n.children, rows, depth + 1);
        }
      });
      return rows;
    };

    flatten(treeData).forEach(row => {
      const r = ws.addRow(row);
      r.eachCell((c, i) => { if (i >= 4) c.numFmt = '#,##0.000'; });
    });

    if (totals) {
      const totalRow = ws.addRow([t.total, '', '', totals.openingDebit, totals.openingCredit, totals.periodDebit, totals.periodCredit, totals.closingDebit, totals.closingCredit, totals.closingDebit - totals.closingCredit]);
      totalRow.eachCell((c, i) => {
        c.font = { bold: true };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC107' } };
        if (i >= 4) c.numFmt = '#,##0.000';
      });
    }

    ws.columns = [{ width: 8 }, { width: 12 }, { width: 40 }, { width: 14 }, { width: 14 }, { width: 14 }, { width: 14 }, { width: 14 }, { width: 14 }, { width: 14 }];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `HierarchicalTrialBalance_${new Date().toISOString().split('T')[0]}.xlsx`;
    a.click();
    toast.success('Excel exported');
  };

  // Export to PDF
  const exportToPDF = () => {
    if (!treeData.length) return;
    const doc = new jsPDF({ orientation: 'landscape' });
    const pageWidth = doc.internal.pageSize.getWidth();

    // Add Logo
    if (companyInfo?.logoBase64) {
      try {
        doc.addImage(companyInfo.logoBase64, 'PNG', 10, 5, 25, 25);
      } catch (e) { console.error('Error adding logo:', e); }
    }

    doc.setFontSize(14);
    doc.text(companyInfo?.companyName || 'Company', pageWidth / 2, 12, { align: 'center' });
    doc.setFontSize(12);
    doc.text(t.title, pageWidth / 2, 20, { align: 'center' });

    const flatten = (nodes: TreeNode[], rows: any[] = [], depth = 0): any[] => {
      nodes.forEach(n => {
        if (n.accountLevel <= maxLevel) {
          const isFinal = !n.hasChildren;
          rows.push([
            `L${n.accountLevel}`, n.accountCode,
            '  '.repeat(depth) + (isRTL ? n.accountNameAr : n.accountName),
            formatNum(isFinal ? n.closingDebit : n.aggregatedClosingDebit),
            formatNum(isFinal ? n.closingCredit : n.aggregatedClosingCredit),
            formatNum(isFinal ? n.balance : n.aggregatedBalance),
          ]);
          if (n.children.length) flatten(n.children, rows, depth + 1);
        }
      });
      return rows;
    };

    const data = flatten(treeData);
    if (totals) {
      data.push([t.total, '', '', formatNum(totals.closingDebit), formatNum(totals.closingCredit), formatNum(totals.closingDebit - totals.closingCredit)]);
    }

    autoTable(doc, {
      head: [[t.level, t.code, t.accountName, t.closingDr, t.closingCr, t.balance]],
      body: data,
      startY: 28,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [21, 101, 192] },
    });

    doc.save(`HierarchicalTrialBalance_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF exported');
  };

  // Print
  const handlePrint = () => {
    if (!treeData.length) return;

    const flatten = (nodes: TreeNode[], html = '', depth = 0): string => {
      nodes.forEach(n => {
        if (n.accountLevel <= maxLevel) {
          if (!includeZeroBalances &&
              n.aggregatedClosingDebit === 0 &&
              n.aggregatedClosingCredit === 0 &&
              n.aggregatedBalance === 0) return;
          const isFinal = !n.hasChildren;
          const style = levelStyles[n.accountLevel as keyof typeof levelStyles] || levelStyles[5];
          html += `<tr style="background:${style.bg};color:${style.color};font-weight:${style.fontWeight};">
            <td style="text-align:center;">L${n.accountLevel}</td>
            <td>${n.accountCode}</td>
            <td style="padding-${isRTL ? 'right' : 'left'}:${depth * 15}px;">${isRTL ? n.accountNameAr : n.accountName}</td>
            <td class="n">${formatNum(isFinal ? n.closingDebit : n.aggregatedClosingDebit)}</td>
            <td class="n">${formatNum(isFinal ? n.closingCredit : n.aggregatedClosingCredit)}</td>
            <td class="n" style="font-weight:bold;">${formatNum(isFinal ? n.balance : n.aggregatedBalance)}</td>
          </tr>`;
          if (n.children.length) html = flatten(n.children, html, depth + 1);
        }
      });
      return html;
    };

    const html = `<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}"><head><title>${t.title}</title>
      <style>body{font-family:Arial;margin:15px;font-size:11px;}.header{text-align:center;margin-bottom:15px;}
      .header img{max-height:60px;margin-bottom:8px;}
      table{width:100%;border-collapse:collapse;}th,td{border:1px solid #ccc;padding:4px;}
      th{background:#1565C0;color:#fff;}.n{text-align:right;font-family:monospace;}
      .total{background:#FFC107;font-weight:bold;}@media print{body{margin:0;}}</style></head>
      <body><div class="header">
      ${companyInfo?.logoBase64 ? `<img src="${companyInfo.logoBase64}" alt="Logo"/>` : ''}
      <h2>${companyInfo?.companyName || ''}</h2><h3>${t.title}</h3></div>
      <table><thead><tr><th>${t.level}</th><th>${t.code}</th><th>${t.accountName}</th>
      <th>${t.closingDr}</th><th>${t.closingCr}</th><th>${t.balance}</th></tr></thead>
      <tbody>${flatten(treeData)}
      ${totals ? `<tr class="total"><td colspan="3">${t.total}</td>
        <td class="n">${formatNum(totals.closingDebit)}</td>
        <td class="n">${formatNum(totals.closingCredit)}</td>
        <td class="n">${formatNum(totals.closingDebit - totals.closingCredit)}</td></tr>` : ''}
      </tbody></table></body></html>`;

    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); setTimeout(() => { w.print(); w.close(); }, 400); }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullscreen}
      maxWidth={false}
      PaperProps={{ sx: fullscreen ? {} : { width: '95vw', height: '90vh', maxWidth: 1600 } }}
      sx={{ zIndex: 9999 }}
    >
      {/* Compact Header */}
      <DialogTitle sx={{ bgcolor: 'primary.dark', color: 'white', py: 0.8, px: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountTree fontSize="small" />
          <Typography variant="subtitle1" fontWeight="bold">{t.title}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" onClick={() => setFullscreen(!fullscreen)} sx={{ color: 'white' }}>
            {fullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
          </IconButton>
          <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}><Close fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: isDark ? '#121212' : '#fafafa' }}>
        {/* Compact Filters */}
        <Paper sx={{ p: 1, mb: 1, bgcolor: isDark ? '#1e1e1e' : '#fff' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <TextField select size="small" label={t.fiscalPeriod} sx={{ minWidth: 150 }}
              value={fiscalPeriodId || ''} onChange={(e) => setFiscalPeriodId(e.target.value ? Number(e.target.value) : undefined)}>
              <MenuItem value="">{t.selectPeriod}</MenuItem>
              {fiscalPeriods.map(p => <MenuItem key={p.fiscalPeriodId} value={p.fiscalPeriodId}>{p.periodName}</MenuItem>)}
            </TextField>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t.fromDate}
                value={fromDate ? new Date(fromDate) : null}
                onChange={(date) => {
                  const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                  setFromDate(newValue);
                  if (toDate && newValue && toDate < newValue) setToDate('');
                }}
                format="dd/MM/yyyy"
                views={['year', 'month', 'day']}
                openTo="day"
                minDate={minDate}
                maxDate={toDate ? new Date(toDate) : maxDate}
                slotProps={{ textField: { size: 'small', fullWidth: true, sx: { width: 130 } } }}
              />
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t.toDate}
                value={toDate ? new Date(toDate) : null}
                onChange={(date) => {
                  const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                  setToDate(newValue);
                  if (fromDate && newValue && fromDate > newValue) setFromDate('');
                }}
                format="dd/MM/yyyy"
                views={['year', 'month', 'day']}
                openTo="day"
                minDate={fromDate ? new Date(fromDate) : minDate}
                maxDate={maxDate}
                slotProps={{ textField: { size: 'small', fullWidth: true, sx: { width: 130 } } }}
              />
            </LocalizationProvider>

            <Box sx={{ width: 140 }}>
              <Typography variant="caption" color="textSecondary">{t.levels}: 1-{maxLevel}</Typography>
              <Slider value={maxLevel} onChange={(_, v) => setMaxLevel(v as number)} min={1} max={5} step={1} size="small" marks />
            </Box>

            <FormControlLabel sx={{ ml: 0 }}
              control={<Checkbox size="small" checked={includeZeroBalances} onChange={(e) => setIncludeZeroBalances(e.target.checked)} />}
              label={<Typography variant="caption">{t.includeZero}</Typography>} />

            <Divider orientation="vertical" flexItem />

            <Button variant="contained" size="small" onClick={loadReport} startIcon={<RefreshIcon />} disabled={loading}>{t.refresh}</Button>
            <Button variant="outlined" size="small" onClick={expandAll} startIcon={<UnfoldMore />}>{t.expandAll}</Button>
            <Button variant="outlined" size="small" onClick={collapseAll} startIcon={<UnfoldLess />}>{t.collapseAll}</Button>

            <Divider orientation="vertical" flexItem />

            <Tooltip title={t.exportExcel}><IconButton size="small" color="success" onClick={exportToExcel} disabled={!treeData.length}><ExcelIcon /></IconButton></Tooltip>
            <Tooltip title={t.exportPdf}><IconButton size="small" color="error" onClick={exportToPDF} disabled={!treeData.length}><PdfIcon /></IconButton></Tooltip>
            <Tooltip title={t.print}><IconButton size="small" color="info" onClick={handlePrint} disabled={!treeData.length}><PrintIcon /></IconButton></Tooltip>
          </Box>
        </Paper>

        {/* Main Table */}
        <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto', bgcolor: isDark ? '#1e1e1e' : '#fff' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress size={50} /></Box>
          ) : !treeData.length ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><Typography color="textSecondary">{t.noData}</Typography></Box>
          ) : (
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#0D47A1', color: '#fff', fontWeight: 'bold', width: 50, textAlign: 'center' }}>{t.level}</TableCell>
                  <TableCell sx={{ bgcolor: '#0D47A1', color: '#fff', fontWeight: 'bold', width: 130 }}>{t.code}</TableCell>
                  <TableCell sx={{ bgcolor: '#0D47A1', color: '#fff', fontWeight: 'bold' }}>{t.accountName}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#2E7D32', color: '#fff', fontWeight: 'bold', width: 115 }}>{t.openingDr}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#C62828', color: '#fff', fontWeight: 'bold', width: 115 }}>{t.openingCr}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#2E7D32', color: '#fff', fontWeight: 'bold', width: 115 }}>{t.periodDr}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#C62828', color: '#fff', fontWeight: 'bold', width: 115 }}>{t.periodCr}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#1B5E20', color: '#fff', fontWeight: 'bold', width: 125 }}>{t.closingDr}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#B71C1C', color: '#fff', fontWeight: 'bold', width: 125 }}>{t.closingCr}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#FF6F00', color: '#fff', fontWeight: 'bold', width: 135 }}>{t.balance}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {renderRows(treeData)}
                {totals && (
                  <TableRow sx={{ bgcolor: '#FFC107', position: 'sticky', bottom: 0 }}>
                    <TableCell colSpan={3} sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{t.total}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{formatNum(totals.openingDebit)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{formatNum(totals.openingCredit)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{formatNum(totals.periodDebit)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{formatNum(totals.periodCredit)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{formatNum(totals.closingDebit)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{formatNum(totals.closingCredit)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '1rem' }}>{formatNum(totals.closingDebit - totals.closingCredit)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {reportData && (
          <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'space-between', px: 1 }}>
            <Typography variant="caption" color="textSecondary">{reportData.data.length} {t.accounts} | {t.levels} 1-{maxLevel}</Typography>
            <Typography variant="caption" color="textSecondary">{reportData.fiscalPeriodName}</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HierarchicalTrialBalanceModal;